import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { CONNECTOR_SCOPE_LABELS, deviceKeyLines, keyStorageLabel, rootScanLine } from '@cerebro-diem/core';
import type { ConnectorDevice, ConnectorRoot, ConnectorScope, PendingPairing } from '@cerebro-diem/core';
import { errorMessage } from '../../services/errors';
import { queryKeys } from '../../services/queries';
import {
  approvePairing,
  denyPairing,
  lookupPairing,
  renameConnector,
  revokeConnector,
  setRootApproved,
  useConnectorRoots,
  useConnectors,
} from '../../services/connectors';
import { inputClass, outlineButton, primaryButton } from '../../components/styles';

const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });
const scopeLabel = (scope: string) => CONNECTOR_SCOPE_LABELS[scope as ConnectorScope] ?? scope;

/**
 * Settings > Connectors: the programs this user runs on their own computers.
 *
 * A computer is paired, never given a token. It makes a key inside its own key
 * store, which cannot export it, shows a short code, and this is where its
 * owner reads back what it claims and approves it. Approving is the only thing
 * that binds that key to this account, and revoking takes it away at once.
 */
export default function ConnectorsSetting() {
  const queryClient = useQueryClient();
  const connectors = useConnectors();
  const roots = useConnectorRoots();
  // Revoking, approving and withdrawing change what every linked folder says.
  const refresh = () =>
    Promise.all([queryKeys.connectors, queryKeys.allFolderBindings].map((queryKey) => queryClient.invalidateQueries({ queryKey })));

  // Closed by default: one line saying how many computers are connected; opened on purpose for the details.
  const [open, setOpen] = useState(false);
  const active = (connectors.data ?? []).filter((c) => c.revoked_at === null).length;
  const summary = connectors.isSuccess
    ? active === 0
      ? 'No computers connected.'
      : `${active} ${active === 1 ? 'computer' : 'computers'} connected.`
    : connectors.isError
      ? 'Your connectors could not be loaded.'
      : 'Checking…';
  if (!open) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <p className={`text-sm flex-1 ${connectors.isError ? 'text-error' : 'text-on-surface-variant'}`}>{summary}</p>
        <button type="button" aria-expanded={false} onClick={() => setOpen(true)} className={outlineButton}>
          Show
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm flex-1 text-on-surface-variant">{summary}</p>
        <button type="button" aria-expanded onClick={() => setOpen(false)} className={outlineButton}>
          Hide
        </button>
      </div>
      <p className="text-sm text-on-surface-variant">
        A connector runs on one of your computers and reports what is in the folders you approve (names, sizes and dates, never
        contents), so a project, area or resource can show its folder. Each computer gets its own, and its key stays on it: there is
        nothing to copy, paste or keep secret here.
      </p>
      <PairComputer connectors={connectors.data ?? []} onPaired={refresh} />
      {connectors.isError && <p className="text-sm text-error">Could not load your connectors: {errorMessage(connectors.error)}</p>}
      {roots.isError && <p className="text-sm text-error">Could not load their folders: {errorMessage(roots.error)}</p>}
      {connectors.isSuccess && connectors.data.length === 0 && <p className="text-sm text-on-surface-variant">No connectors yet.</p>}
      <ul className="space-y-3">
        {(connectors.data ?? []).map((connector) => (
          <ConnectorItem
            key={connector.connector_id}
            connector={connector}
            roots={(roots.data ?? []).filter((root) => root.connector_id === connector.connector_id)}
            onChanged={refresh}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * Pair a computer: type the code it shows, read back what it claims, and
 * approve or deny it. Looking a code up binds nothing; only Approve does.
 */
function PairComputer({ connectors, onPaired }: { connectors: ConnectorDevice[]; onPaired: () => Promise<unknown> }) {
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<PendingPairing | null>(null);
  const [name, setName] = useState('');
  const [replacing, setReplacing] = useState('');

  const lookup = useMutation({
    mutationFn: () => lookupPairing(code),
    onSuccess: (found) => {
      setPending(found);
      setName(found.proposed_name);
      setReplacing('');
    },
  });
  const done = async () => {
    setPending(null);
    setCode('');
    await onPaired();
  };
  const approve = useMutation({
    mutationFn: () => approvePairing(pending!.user_code, name, replacing === '' ? null : replacing),
    onSuccess: done,
  });
  const deny = useMutation({ mutationFn: () => denyPairing(pending!.user_code), onSuccess: done });

  if (pending) {
    const failed = [approve, deny].find((m) => m.isError);
    return (
      <div role="region" aria-label="Pair a computer" className="rounded-xl border border-primary/40 p-3 space-y-2">
        <p className="text-sm font-medium">A computer is waiting under {pending.user_code}. Check it against the one in front of you:</p>
        <ul className="text-sm text-on-surface-variant space-y-1">
          <li>It calls itself &quot;{pending.proposed_name}&quot; and reads file ids in {pending.runtime}.</li>
          <li>It asks to {pending.scopes.map(scopeLabel).join(' and ').toLowerCase()}.</li>
          <li>
            Its key is in {keyStorageLabel(pending.key_storage)}, which cannot export it
            {pending.unlock_required ? ', and it will ask you to unlock it at every use' : ''}.
          </li>
          <li>
            Its key&apos;s fingerprint is <code className="break-all">{pending.key_id}</code>. That computer shows the same one.
          </li>
        </ul>
        <div className="space-y-2">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Connector name"
            maxLength={80}
            required
          />
          <label className="block text-sm">
            <span className="text-on-surface-variant">This computer is</span>
            <select
              className={`${inputClass} mt-1`}
              value={replacing}
              onChange={(e) => setReplacing(e.target.value)}
              aria-label="Which connector this computer is"
            >
              <option value="">a new connector</option>
              {connectors
                .filter((connector) => connector.revoked_at === null)
                .map((connector) => (
                  <option key={connector.connector_id} value={connector.connector_id}>
                    {connector.name} again (its folders stay; its old key stops working)
                  </option>
                ))}
            </select>
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => approve.mutate()} disabled={approve.isPending} className={primaryButton}>
            Approve
          </button>
          <button type="button" onClick={() => deny.mutate()} disabled={deny.isPending} className={`${outlineButton} text-error`}>
            Deny
          </button>
          <button type="button" onClick={() => setPending(null)} className={outlineButton}>
            Cancel
          </button>
        </div>
        {failed && <p className="text-sm text-error">Not done: {errorMessage(failed.error)}</p>}
      </div>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    lookup.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-2" aria-label="Pair a computer">
      <div className="flex flex-wrap gap-2">
        <input
          className={`${inputClass} min-w-0 flex-1 basis-full sm:basis-0`}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="The code that computer shows, e.g. H4K2-9PQR"
          aria-label="Pairing code"
          maxLength={9}
          required
        />
        <button type="submit" disabled={lookup.isPending} className={primaryButton}>
          {lookup.isPending ? 'Looking…' : 'Pair a computer'}
        </button>
      </div>
      <p className="text-xs text-on-surface-variant">
        Run <code>cerebro-connector pair</code> on that computer. It makes a key that cannot leave it and shows you a code.
      </p>
      {lookup.isError && <p className="text-sm text-error">Not found: {errorMessage(lookup.error)}</p>}
    </form>
  );
}

function ConnectorItem({ connector, roots, onChanged }: { connector: ConnectorDevice; roots: ConnectorRoot[]; onChanged: () => Promise<unknown> }) {
  const [renaming, setRenaming] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  // The key's fingerprint and where it is kept: there for whoever needs it, not in everyone's way.
  const [details, setDetails] = useState(false);
  const reportsFolders = connector.scopes.includes('files:report');
  const [name, setName] = useState(connector.name);
  const rename = useMutation({
    mutationFn: () => renameConnector(connector.connector_id, name),
    onSuccess: async () => {
      await onChanged();
      setRenaming(false);
    },
  });
  const revoke = useMutation({ mutationFn: () => revokeConnector(connector.connector_id), onSuccess: onChanged });
  const approval = useMutation({
    mutationFn: ({ rootKey, approved }: { rootKey: string; approved: boolean }) =>
      setRootApproved(connector.connector_id, rootKey, approved),
    onSuccess: onChanged,
  });
  const revoked = connector.revoked_at !== null;
  const failed = [rename, revoke, approval].find((m) => m.isError);

  return (
    <li className="rounded-xl bg-surface-variant/30 p-3 space-y-2" aria-label={`Connector ${connector.name}`}>
      {renaming ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate();
          }}
          className="flex flex-wrap gap-2"
        >
          <input
            className={`${inputClass} min-w-0 flex-1`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label={`New name for ${connector.name}`}
            maxLength={80}
            required
          />
          <button type="submit" disabled={rename.isPending} className={primaryButton}>
            Save
          </button>
          <button type="button" onClick={() => setRenaming(false)} className={outlineButton}>
            Cancel
          </button>
        </form>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium break-words">{connector.name}</p>
          {revoked && <span className="text-xs font-medium text-error">Revoked {ago(connector.revoked_at!)}</span>}
        </div>
      )}
      <p className="text-sm text-on-surface-variant">
        Can: {connector.scopes.map(scopeLabel).join(' · ').toLowerCase()} · {connector.last_seen_at ? `Last seen ${ago(connector.last_seen_at)}` : 'Not seen yet'}
      </p>
      {/* Revoked, or no computer paired: that is said outright. A working key's fingerprint waits behind Details. */}
      {revoked || connector.key_id === null ? (
        deviceKeyLines(connector, ago).map((line) => (
          <p key={line} className="text-sm text-on-surface-variant break-words">
            {line}
          </p>
        ))
      ) : (
        <>
          <button type="button" aria-expanded={details} onClick={() => setDetails(!details)} className="text-xs text-primary underline">
            {details ? 'Hide details' : 'Details'}
          </button>
          {details && (
            <>
              <p className="text-sm text-on-surface-variant">Added {ago(connector.created_at)}.</p>
              {deviceKeyLines(connector, ago).map((line) => (
                <p key={line} className="text-sm text-on-surface-variant break-words">
                  {line}
                </p>
              ))}
            </>
          )}
        </>
      )}

      {roots.length > 0 && (
        <ul className="space-y-2" aria-label={`Folders ${connector.name} offers`}>
          {roots.map((root) => (
            <li key={root.root_key} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium">{root.label}</span>
              <span className="text-on-surface-variant">({root.root_key})</span>
              <span className="basis-full text-on-surface-variant">{rootScanLine(root, ago)}</span>
              {!revoked && (
                <button
                  type="button"
                  disabled={approval.isPending}
                  onClick={() => approval.mutate({ rootKey: root.root_key, approved: root.approved_at === null })}
                  className={root.approved_at === null ? primaryButton : outlineButton}
                  aria-label={`${root.approved_at === null ? 'Approve' : 'Withdraw approval for'} ${root.label}`}
                >
                  {root.approved_at === null ? 'Approve' : 'Withdraw approval'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {!revoked && reportsFolders && roots.length === 0 && (
        <p className="text-sm text-on-surface-variant">It has not offered any folders yet. Run its offer command on that computer.</p>
      )}

      {!revoked && !renaming && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setName(connector.name);
              setRenaming(true);
            }}
            className={outlineButton}
          >
            Rename
          </button>
          {confirmRevoke ? (
            <>
              <span className="text-sm">
                Revoke {connector.name}? Its key stops working at once, and it cannot be undone. Pair that computer again to give it a
                new one.
              </span>
              <button type="button" onClick={() => revoke.mutate()} disabled={revoke.isPending} className={`${outlineButton} text-error`}>
                Revoke now
              </button>
              <button type="button" onClick={() => setConfirmRevoke(false)} className={outlineButton}>
                Keep it
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setConfirmRevoke(true)} className={`${outlineButton} text-error`}>
              Revoke
            </button>
          )}
        </div>
      )}
      {failed && <p className="text-sm text-error">Not saved: {errorMessage(failed.error)}</p>}
    </li>
  );
}
